import { Box, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { tokens } from '../../theme/theme';
import { severityColor, formatClock } from '../../utils/severity';

/**
 * The Pulse Strip is Melsoft CyberGuard's signature element: a persistent,
 * always-scrolling monospace log of live events beneath the top bar. It
 * exists on every page so the console never reads as a static report — the
 * platform is always watching.
 */
export default function PulseStrip() {
  const feed = useSelector((s) => s.dashboard.liveFeed);
  const items = (feed && feed.length ? feed : []).slice(0, 20);

  if (!items.length) return null;

  const track = [...items, ...items]; // duplicate for seamless loop

  return (
    <Box
      sx={{
        height: 34,
        overflow: 'hidden',
        borderBottom: `1px solid ${tokens.border}`,
        backgroundColor: '#0D1320',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        '&::before, &::after': {
          content: '""',
          position: 'absolute',
          top: 0, bottom: 0, width: 48, zIndex: 2, pointerEvents: 'none'
        },
        '&::before': { left: 0, background: 'linear-gradient(90deg, #0D1320, transparent)' },
        '&::after': { right: 0, background: 'linear-gradient(270deg, #0D1320, transparent)' }
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          whiteSpace: 'nowrap',
          animation: 'mcg-pulse-scroll 42s linear infinite',
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          '@keyframes mcg-pulse-scroll': {
            '0%': { transform: 'translateX(0)' },
            '100%': { transform: 'translateX(-50%)' }
          }
        }}
      >
        {track.map((evt, i) => (
          <Box key={`${evt.id}-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 2 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: severityColor(evt.severity), flexShrink: 0 }} />
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.72rem',
                color: tokens.text.secondary
              }}
            >
              <Box component="span" sx={{ color: tokens.text.muted }}>{formatClock(evt.timestamp)}</Box>
              {'  '}
              <Box component="span" sx={{ color: severityColor(evt.severity), fontWeight: 600 }}>
                {evt.severity.toUpperCase()}
              </Box>
              {'  '}{evt.type} · {evt.sourceIp} → {evt.target} · {evt.status}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
